class propertyFinder {
    public static getProperty(object: Object, property: string): PropertyDescriptor {
        let obj = object;
        do {
            let desc = Object.getOwnPropertyDescriptor(obj, property);
            if (desc)
                return desc;
        } while ((obj = Object.getPrototypeOf(obj)) != null)
        return null;
    }
}

class BinderPropertyDescriptor {
    public source: Object;
    public propertyName: string;
    private property: PropertyDescriptor;
    constructor(source: object, propertyName: string) {
        this.source = source;
        this.propertyName = propertyName;
    }

    public createBindingPlatform() {
        let relatedBindingListName = `__BINDER__${this.propertyName}`;
        if (this.source[relatedBindingListName] == null) {
            let relatedPropertyDescListName = `__BINDER__${this.propertyName}__DESC`;
            this.source[relatedBindingListName] = [];
            this.source[relatedPropertyDescListName] = [];
            let protoProperty = propertyFinder.getProperty(this.source, this.propertyName);
            if (protoProperty == null) {
                console.log(`can not find property ${this.propertyName}`)
            }
            let setter = protoProperty.set;
            let getter = protoProperty.get;
            if (getter == null && setter == null) {
                console.error(`Can not find ${this.propertyName} on ${this.source.constructor.name}`)
            }
            Object.defineProperty(this.source, this.propertyName, {
                get: getter,
                set: function (x) {
                    let getterValue = getter.call(this) ?? null;
                    x = x ?? null;
                    let isEqual = getterValue == x;
                    if (getterValue === "" && x === 0)
                        isEqual = false;
                    if (x === "" && (getterValue === 0))
                        isEqual = false;
                    let cond1 = getterValue != x;
                    if (isEqual === false) {
                        setter.call(this, x);
                        this[relatedBindingListName].forEach((binding, i) => {
                            binding.changed(this[relatedPropertyDescListName][i]);
                        });
                    }
                },
            });
            this.property = propertyFinder.getProperty(this.source, this.propertyName);
        }
    }
    registerBinding(binding: Binding) {
        let relatedBindingListName = `__BINDER__${this.propertyName}`;
        let relatedPropertyDescListName = `__BINDER__${this.propertyName}__DESC`;
        if (this.source[relatedBindingListName] != null) {
            let bindings = this.source[relatedBindingListName];
            let desc = this.source[relatedPropertyDescListName];
            bindings.push(binding);
            desc.push(this);
        }
    }
    releaseBinding(binding: Binding) {
        let relatedBindingListName = `__BINDER__${this.propertyName}`;
        let relatedPropertyDescListName = `__BINDER__${this.propertyName}__DESC`;
        if (this.source[relatedBindingListName] != null) {
            let bindings = this.source[relatedBindingListName];
            let desc = this.source[relatedPropertyDescListName];
            let index = bindings.indexOf(binding);
            if (index > -1) {
                bindings.splice(index, 1);
                desc.splice(index, 1);
            }
        }
    }
    getValue(): any {
        return this.source[this.propertyName];
    }

    setValue(value: any) {
        if (value != Binder.OmitChangeValue)
            this.source[this.propertyName] = value;
    }
}

class StaticBinderPropertyDescriptor extends BinderPropertyDescriptor {
    public value: any;
    constructor(value: any) {
        super(null, null);
        this.value = value;
    }
    public override createBindingPlatform() {

    }
    public override registerBinding(binding: Binding) {

    }
    public override releaseBinding(binding: Binding) {

    }
    public override getValue(): any {
        return this.value;
    }
    public override setValue(value: any) {

    }
}
interface IBinding {
    changed(changedProperty: BinderPropertyDescriptor);
    release();
    create();
}
class EmptyBinding implements IBinding {
    changed(changedProperty: BinderPropertyDescriptor) {
    }
    release() {
    }
    create() {
    }
}
class Binding implements IBinding {
    public destination: BinderPropertyDescriptor;
    public sources: BinderPropertyDescriptor[];
    public converter: IConverter;
    private isExecuting: boolean;

    constructor() {
        this.isExecuting = false;
    }
    public changed(changedProperty: BinderPropertyDescriptor) {
        if (this.destination == null) {
            this.release();
        } else {
            if (this.isExecuting == false) {
                this.isExecuting = true;
                if (changedProperty == this.destination)
                    this.runBackward();
                else
                    this.runForward();
                this.isExecuting = false;
            }
        }
    }
    private runForward() {
        let currentDestinationValue;
        let newValue;
        this.writeForward();
        do {
            currentDestinationValue = this.destination.getValue()
            this.writeBackward();
            this.writeForward();
            newValue = this.destination.getValue();
            if (typeof newValue == 'number' && typeof currentDestinationValue == 'number' && isNaN(newValue) && isNaN(currentDestinationValue)) {
                break;
            }
        } while (newValue != currentDestinationValue);
    }
    private runBackward() {
        this.writeBackward();
        this.runForward();
    }

    private writeForward() {
        this.destination.setValue(this.converter.convert(...this.sources.map(x => x.getValue())));
    }
    private writeBackward() {
        let sourceResults = this.converter.convertBack(this.destination.getValue(), ...this.sources);
        sourceResults.forEach((x, i) => {
            let relatedDescriptor = this.sources[i];
            relatedDescriptor.setValue(x);
        })
    }

    public release() {
        if (this.destination) this.destination.releaseBinding(this);
        if (this.sources) this.sources.forEach(x => x.releaseBinding(this));
        this.destination = null;
        this.sources = null;
    }
    public create() {
        this.destination.createBindingPlatform();
        this.sources.forEach(x => x.createBindingPlatform());
        this.destination.registerBinding(this);
        this.sources.forEach(x => x.registerBinding(this));

        this.runForward();
    }

}
interface IConverter {
    convert(...sources): any;
    convertBack(value, ...sources: BinderPropertyDescriptor[]): any[];
}
class SymmetricCoverter implements IConverter {
    convert(...sources: any[]) {
        return sources[0];
    }
    convertBack(value: any, ...sources: BinderPropertyDescriptor[]): any[] {
        return [value];
    }
}
class Binder {
    public static readonly OmitChangeValue: Object = new Object();

    public static createBinding(destination: BinderPropertyDescriptor, converter: IConverter, ...sources: BinderPropertyDescriptor[]): IBinding {
        let binding = new Binding();
        binding.converter = converter || new SymmetricCoverter();
        binding.destination = destination;
        binding.sources = sources;
        binding.create();
        return binding;
    }
    public static createEmptyBinding() {
        return new EmptyBinding();
    }
}
export { Binder, BinderPropertyDescriptor, StaticBinderPropertyDescriptor, IConverter, IBinding }